import { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { RefreshCw, Users, MousePointerClick, MapPin, Calendar as CalIcon, LogOut } from "lucide-react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    Legend,
} from "recharts";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { fetchAnalytics } from "@/lib/tracker";
import AdminLogin, { isAdminAuthed, signOutAdmin } from "@/components/AdminLogin";

function toISOStart(d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x.toISOString();
}
function toISOEnd(d) {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x.toISOString();
}

function StatCard({ icon: Icon, label, value, sub, testid }) {
    return (
        <Card
            data-testid={testid}
            className="bg-[#001b6b] border-white/15 text-[#FAF9F6] rounded-none"
        >
            <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-xs font-mono-tight uppercase tracking-widest text-white/70">
                    {label}
                </CardTitle>
                <Icon className="w-4 h-4 text-[#ED1C24]" />
            </CardHeader>
            <CardContent>
                <div className="font-display text-4xl md:text-5xl leading-none">{value}</div>
                {sub && (
                    <div className="mt-2 text-xs font-mono-tight text-white/60 uppercase tracking-wider">
                        {sub}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export default function Admin() {
    const [authed, setAuthed] = useState(() => isAdminAuthed());

    const today = new Date();
    const defaultFrom = new Date();
    defaultFrom.setDate(today.getDate() - 6);

    const [range, setRange] = useState({ from: defaultFrom, to: today });
    const [activePreset, setActivePreset] = useState("7d");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const applyPreset = (key) => {
        const now = new Date();
        if (key === "today") {
            setRange({ from: now, to: now });
        } else if (key === "yesterday") {
            const y = new Date();
            y.setDate(now.getDate() - 1);
            setRange({ from: y, to: y });
        } else if (key === "7d") {
            const f = new Date();
            f.setDate(now.getDate() - 6);
            setRange({ from: f, to: now });
        } else if (key === "max") {
            // Very early date — covers everything
            setRange({ from: new Date("2020-01-01"), to: now });
        }
        setActivePreset(key);
    };

    const load = useCallback(async () => {
        if (!authed) return;
        setLoading(true);
        setError(null);
        try {
            const from = range?.from ? toISOStart(range.from) : undefined;
            const to = range?.to ? toISOEnd(range.to) : undefined;
            const res = await fetchAnalytics(from, to);
            setData(res);
        } catch (e) {
            setError(e?.message || "Failed to load");
        } finally {
            setLoading(false);
        }
    }, [range, authed]);

    useEffect(() => {
        load();
    }, [load]);

    const totals = data?.totals || {};
    const hourlyClicks = data?.hourly_clicks || [];
    const locations = data?.locations || [];

    const rangeLabel = useMemo(() => {
        if (range?.from && range?.to)
            return `${format(range.from, "dd MMM yyyy")} → ${format(range.to, "dd MMM yyyy")}`;
        if (range?.from) return `${format(range.from, "dd MMM yyyy")} → …`;
        return "Pilih tarikh";
    }, [range]);

    if (!authed) {
        return <AdminLogin onAuthed={() => setAuthed(true)} />;
    }

    return (
        <div
            data-testid="admin-dashboard"
            className="min-h-screen bg-[#0044FE] text-[#FAF9F6] grain"
        >
            <div className="mx-auto max-w-7xl px-5 md:px-10 py-10 md:py-14">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                    <div>
                        <h1 className="font-display uppercase text-[10vw] sm:text-6xl md:text-7xl leading-[0.9]">
                            SUPERZONE
                            <br />
                            <span style={{ color: "#ED1C24" }}>ANALYTICS.</span>
                        </h1>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap justify-end">
                        {/* Preset range buttons */}
                        <div className="flex flex-wrap gap-2" data-testid="preset-range-group">
                            {[
                                { key: "today", label: "Today" },
                                { key: "yesterday", label: "Yesterday" },
                                { key: "7d", label: "Last 7 days" },
                                { key: "max", label: "Maximum" },
                            ].map((p) => {
                                const active = activePreset === p.key;
                                return (
                                    <button
                                        key={p.key}
                                        onClick={() => applyPreset(p.key)}
                                        data-testid={`preset-${p.key}`}
                                        className={`px-3 py-2 border-2 font-mono-tight text-[10px] tracking-widest uppercase transition-all ${
                                            active
                                                ? "bg-[#ED1C24] border-white text-white"
                                                : "bg-transparent border-white/40 text-white/80 hover:bg-white/10"
                                        }`}
                                    >
                                        {p.label}
                                    </button>
                                );
                            })}
                        </div>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    className="rounded-none bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white"
                                    data-testid="admin-date-picker-button"
                                >
                                    <CalIcon className="w-4 h-4 mr-2" />
                                    {rangeLabel}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent
                                align="end"
                                className="w-auto p-0 bg-[#001b6b] border border-white/20 text-white rounded-none"
                            >
                                <Calendar
                                    mode="range"
                                    numberOfMonths={2}
                                    selected={range}
                                    onSelect={(r) => {
                                        setRange(r || { from: undefined, to: undefined });
                                        setActivePreset("custom");
                                    }}
                                    className="text-white"
                                />
                            </PopoverContent>
                        </Popover>
                        <Button
                            onClick={load}
                            disabled={loading}
                            className="rounded-none bg-[#ED1C24] hover:bg-[#ff2a33] border-2 border-white/70"
                            data-testid="admin-refresh-button"
                        >
                            <RefreshCw
                                className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`}
                            />
                            REFRESH
                        </Button>
                        <Button
                            onClick={() => {
                                signOutAdmin();
                                setAuthed(false);
                            }}
                            variant="outline"
                            className="rounded-none bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white"
                            data-testid="admin-logout-button"
                        >
                            <LogOut className="w-4 h-4 mr-2" />
                            LOGOUT
                        </Button>
                    </div>
                </div>

                {error && (
                    <div className="mt-6 border border-red-400/40 bg-red-500/10 px-4 py-3 text-sm">
                        {error}
                    </div>
                )}

                {/* Stat cards */}
                <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <StatCard
                        testid="stat-unique-visitors"
                        icon={Users}
                        label="Unique Visitors"
                        value={totals.unique_visitors ?? 0}
                    />
                    <StatCard
                        testid="stat-cta-clicks"
                        icon={MousePointerClick}
                        label="CTA Clicks"
                        value={totals.total_clicks ?? 0}
                        sub={`${totals.unique_clickers ?? 0} unique`}
                    />
                    <StatCard
                        testid="stat-conversion"
                        icon={MapPin}
                        label="Conversion"
                        value={`${totals.conversion_rate ?? 0}%`}
                        sub={"clicks / visits"}
                    />
                </div>

                {/* Chart — hourly click distribution */}
                <Card className="mt-8 bg-[#001b6b] border-white/15 text-white rounded-none">
                    <CardHeader>
                        <CardTitle className="font-mono-tight text-xs uppercase tracking-widest text-white/70">
                            CTA Click Activity · 24h Distribution
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={hourlyClicks} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                                <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                                <XAxis
                                    dataKey="label"
                                    stroke="rgba(255,255,255,0.6)"
                                    fontSize={10}
                                    interval={1}
                                />
                                <YAxis
                                    stroke="rgba(255,255,255,0.6)"
                                    fontSize={11}
                                    allowDecimals={false}
                                />
                                <Tooltip
                                    contentStyle={{
                                        background: "#060614",
                                        border: "1px solid rgba(255,255,255,0.2)",
                                        color: "#FAF9F6",
                                        borderRadius: 0,
                                    }}
                                    cursor={{ fill: "rgba(237,28,36,0.12)" }}
                                    labelFormatter={(l) => `Jam ${l}`}
                                />
                                <Legend wrapperStyle={{ color: "rgba(255,255,255,0.75)" }} />
                                <Bar
                                    dataKey="clicks"
                                    name="CTA Clicks"
                                    fill="#ED1C24"
                                    radius={[2, 2, 0, 0]}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>

                {/* Locations */}
                <div className="mt-8">
                    <Card className="bg-[#001b6b] border-white/15 text-white rounded-none">
                        <CardHeader>
                            <CardTitle className="font-mono-tight text-xs uppercase tracking-widest text-white/70">
                                Top Locations
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="max-h-80 overflow-y-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="border-white/10 hover:bg-transparent">
                                            <TableHead className="text-white/70">Country</TableHead>
                                            <TableHead className="text-white/70">City</TableHead>
                                            <TableHead className="text-white/70 text-right">Unique</TableHead>
                                            <TableHead className="text-white/70 text-right">Visits</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody data-testid="locations-table-body">
                                        {locations.length === 0 && (
                                            <TableRow className="border-white/10">
                                                <TableCell colSpan={4} className="text-center text-white/50 py-8">
                                                    Belum ada data
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {locations.map((l, i) => (
                                            <TableRow key={i} className="border-white/10 hover:bg-white/5">
                                                <TableCell>{l.country}</TableCell>
                                                <TableCell>{l.city}</TableCell>
                                                <TableCell className="text-right font-mono-tight">
                                                    {l.unique_visitors}
                                                </TableCell>
                                                <TableCell className="text-right font-mono-tight">
                                                    {l.visits}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="mt-10 text-xs font-mono-tight uppercase tracking-widest text-white/50">
                    Range · {rangeLabel}
                </div>
            </div>
        </div>
    );
}
